import { useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { createCurrentLanguageValue, itemByLanguage } from '@/utils/language';

import InputDialog from '@/components/Dialogs/InputDialog';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants from '@/constants';

import { clonePlan, Plan } from '@/_generated/api-clients/xp';
import { userPrefsAtom } from '@/atoms/userPrefs';
import useIsValidPlanName from '@/routes/TrainingPlans/hooks/useIsValidPlanName';

interface DuplicatePlanProps {
  plan: Plan;
  refetchPlans: () => void;
  setShowDuplicatePlan: (show: boolean) => void;
  showDuplicatePlan: boolean;
}

const DuplicatePlan: React.FC<DuplicatePlanProps> = ({
  plan,
  refetchPlans,
  setShowDuplicatePlan,
  showDuplicatePlan,
}) => {
  const { t } = useTranslation();
  const { language: userLanguage } = useAtomValue(userPrefsAtom);
  const [showInvalidNameError, setShowInvalidNameError] = useState(false);
  const isValidPlanName = useIsValidPlanName({
    onInvalidName: () => setShowInvalidNameError(true),
  });

  const onDuplicatePlan = (name: string) => {
    setShowDuplicatePlan(false);
    const trimmedName = name.trim();
    const planId = plan.id;
    const ownerId = plan.ownerId;
    const ownerType = plan.ownerType;

    if (!isValidPlanName(trimmedName, ownerId)) {
      return;
    }

    const ownerPayload =
      ownerType === Constants.PLAN_OWNER_TYPE.LOCATION
        ? { locationNumber: ownerId }
        : { ownerId };

    clonePlan({
      path: { planId: planId },
      body: {
        name: createCurrentLanguageValue(trimmedName, userLanguage),
        ...ownerPayload,
      },
    })
      .then(() => {
        refetchPlans();
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id}>
            {`${trimmedName} ${t('TrainingPlans.toastMessage.duplicated')}`}
          </ToastMessageBlock>
        ));
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };

  return (
    <>
      <InputDialog
        headerText={t('Generic.giveName', { object: t('Generic.plan') })}
        isDuplicatePlan={showDuplicatePlan}
        isOpen={showDuplicatePlan}
        onClose={() => setShowDuplicatePlan(false)}
        primaryButtonHandler={onDuplicatePlan}
        primaryButtonText={t('Button.create')}
        primaryButtonVariant="filled"
        secondaryButtonHandler={() => setShowDuplicatePlan(false)}
        secondaryButtonText={t('Button.cancel')}
        textInputDefaultValue={`${itemByLanguage(plan.name)} ${t('TrainingPlans.duplicatePlanInputCopy')}`}
        textInputPlaceholder={t('TrainingPlans.planNamePlaceholder')}
      />
      <ConfirmationModal
        bodyText={t('InvalidPlanName.paragraphText')}
        headerText={t('InvalidPlanName.errorHeader')}
        isError={true}
        isOpen={showInvalidNameError}
        onClose={() => setShowInvalidNameError(false)}
        primaryButtonHandler={() => setShowInvalidNameError(false)}
        primaryButtonText={t('Button.close')}
      />
    </>
  );
};
export default DuplicatePlan;
